import { apiCall, apiCallWithMeta } from './api';
import type {
  League,
  Division,
  Season,
  Category,
  CreateLeagueRequest,
  UpdateLeagueRequest,
  CreateSeasonRequest,
  UpdateSeasonRequest,
  CreateDivisionRequest,
  UpdateDivisionRequest,
  PointsConfig,
  DivisionStandings,
  PlayerRanking,
  PromotionPreview,
  ProcessEndOfSeasonResponse,
  StartNewSeasonRequest,
  ReRegisterTeamRequest,
  ReRegisterTeamResponse,
  FormNewTeamRequest,
  Team,
  LeagueAdministrator,
  AssignLeagueAdminRequest,
  TeamStanding,
  SeasonSubscribersResponse,
  Enrollment,
} from '@sulis-liiga/common';
import { API_ROUTES } from '@sulis-liiga/common';

/** Per-league summary stats shown on the Home page snapshot. */
export interface LeagueSummary {
  players: number;
  teams: number;
  games: number;
  season: { id: string; name: string; status: string } | null;
}

const leagueService = {
  /**
   * Get all leagues with optional pagination
   */
  getLeagues: async (params?: {
    page?: number;
    limit?: number;
  }): Promise<{
    data: League[];
    meta?: { page?: number; limit?: number; total?: number; pages?: number };
  }> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());

    const url = `/v1/leagues${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return apiCallWithMeta<League[]>('get', url);
  },

  /**
   * Get league by ID
   */
  getLeague: async (leagueId: string): Promise<League> => {
    return apiCall<League>('get', `/v1/leagues/${leagueId}`);
  },

  /**
   * Public per-league summary stats (players, teams, games, current season) for
   * the Home page snapshot.
   */
  getLeagueSummary: async (leagueId: string): Promise<LeagueSummary> => {
    return apiCall<LeagueSummary>('get', `/v1/leagues/${leagueId}/summary`);
  },

  /**
   * Create a new league
   */
  createLeague: async (leagueData: CreateLeagueRequest): Promise<League> => {
    return apiCall<League>('post', '/v1/leagues', leagueData);
  },

  /**
   * Update a league
   */
  updateLeague: async (leagueId: string, leagueData: UpdateLeagueRequest): Promise<League> => {
    return apiCall<League>('patch', `/v1/leagues/${leagueId}`, leagueData);
  },

  /**
   * Delete a league
   */
  deleteLeague: async (leagueId: string): Promise<void> => {
    return apiCall<void>('delete', `/v1/leagues/${leagueId}`);
  },

  /**
   * Create a new season for a league
   */
  createSeason: async (leagueId: string, seasonData: CreateSeasonRequest): Promise<Season> => {
    return apiCall<Season>('post', `/v1/leagues/${leagueId}/seasons`, seasonData);
  },

  /**
   * Update a season
   */
  updateSeason: async (
    leagueId: string,
    seasonId: string,
    seasonData: UpdateSeasonRequest,
  ): Promise<Season> => {
    return apiCall<Season>('patch', `/v1/leagues/${leagueId}/seasons/${seasonId}`, seasonData);
  },

  /**
   * Get points configuration for a season
   */
  getPointsConfig: async (leagueId: string, seasonId: string): Promise<PointsConfig> => {
    return apiCall<PointsConfig>(
      'get',
      `/v1/leagues/${leagueId}/seasons/${seasonId}/points-config`,
    );
  },

  /**
   * Update points configuration for a season
   */
  updatePointsConfig: async (
    leagueId: string,
    seasonId: string,
    pointsConfig: PointsConfig,
  ): Promise<PointsConfig> => {
    return apiCall<PointsConfig>(
      'patch',
      `/v1/leagues/${leagueId}/seasons/${seasonId}/points-config`,
      pointsConfig,
    );
  },

  /**
   * Create a new division (ladder). The backend generates one Division record per
   * tier in `divisionData.tiers` and returns them all as an array.
   */
  createDivision: async (
    leagueId: string,
    seasonId: string,
    divisionData: CreateDivisionRequest,
  ): Promise<Division[]> => {
    return apiCall<Division[]>(
      'post',
      `/v1/leagues/${leagueId}/seasons/${seasonId}/divisions`,
      divisionData,
    );
  },

  /**
   * Update a division
   */
  updateDivision: async (
    leagueId: string,
    seasonId: string,
    divisionId: string,
    divisionData: UpdateDivisionRequest,
  ): Promise<Division> => {
    return apiCall<Division>(
      'patch',
      `/v1/leagues/${leagueId}/seasons/${seasonId}/divisions/${divisionId}`,
      divisionData,
    );
  },

  /**
   * Delete a division
   */
  deleteDivision: async (leagueId: string, seasonId: string, divisionId: string): Promise<void> => {
    return apiCall<void>(
      'delete',
      `/v1/leagues/${leagueId}/seasons/${seasonId}/divisions/${divisionId}`,
    );
  },

  /**
   * Get all categories for a league
   */
  getCategories: async (leagueId: string): Promise<Category[]> => {
    return apiCall<Category[]>('get', `/v1/leagues/${leagueId}/categories`);
  },

  /**
   * Get divisions by category
   */
  getDivisionsByCategory: async (leagueId: string, categoryId: Category): Promise<Division[]> => {
    return apiCall<Division[]>('get', `/v1/leagues/${leagueId}/categories/${categoryId}/divisions`);
  },

  // ─── Phase 4: Standings & Rankings ───────────────────────────────────────

  /**
   * Get standings for a division
   */
  getDivisionStandings: async (
    leagueId: string,
    seasonId: string,
    divisionId: string,
  ): Promise<DivisionStandings> => {
    return apiCall<DivisionStandings>(
      'get',
      `/v1/leagues/${leagueId}/seasons/${seasonId}/divisions/${divisionId}/standings`,
    );
  },

  /**
   * Get player rankings for a division
   */
  getPlayerRankings: async (
    leagueId: string,
    seasonId: string,
    divisionId: string,
  ): Promise<PlayerRanking[]> => {
    return apiCall<PlayerRanking[]>(
      'get',
      `/v1/leagues/${leagueId}/seasons/${seasonId}/divisions/${divisionId}/player-rankings`,
    );
  },

  /**
   * Recalculate standings for a division (admin)
   */
  recalculateStandings: async (
    leagueId: string,
    seasonId: string,
    divisionId: string,
  ): Promise<{ updated: number }> => {
    return apiCall<{ updated: number }>(
      'post',
      `/v1/leagues/${leagueId}/seasons/${seasonId}/divisions/${divisionId}/standings/recalculate`,
    );
  },

  // ─── Phase 4: Season Transitions ─────────────────────────────────────────

  /**
   * Preview promotion/relegation for a season (season-scoped).
   * GET /v1/leagues/:leagueId/seasons/:seasonId/promotion-preview
   */
  getPromotionPreview: async (leagueId: string, seasonId: string): Promise<PromotionPreview> => {
    return apiCall<PromotionPreview>(
      'get',
      `/v1/leagues/${leagueId}/seasons/${seasonId}/promotion-preview`,
    );
  },

  /**
   * Finalize a season: archive standings, expire points, mark COMPLETED.
   * POST /v1/leagues/:leagueId/seasons/:seasonId/finalize
   */
  finalizeSeason: async (
    leagueId: string,
    seasonId: string,
  ): Promise<ProcessEndOfSeasonResponse> => {
    return apiCall<ProcessEndOfSeasonResponse>(
      'post',
      `/v1/leagues/${leagueId}/seasons/${seasonId}/finalize`,
    );
  },

  /**
   * Start a new season
   */
  startNewSeason: async (leagueId: string, seasonData: StartNewSeasonRequest): Promise<Season> => {
    return apiCall<Season>('post', `/v1/leagues/${leagueId}/seasons/new`, seasonData);
  },

  /**
   * Archive a season
   */
  archiveSeason: async (
    leagueId: string,
    seasonId: string,
  ): Promise<{ seasonId: string; archived: boolean }> => {
    return apiCall<{ seasonId: string; archived: boolean }>(
      'post',
      `/v1/leagues/${leagueId}/seasons/${seasonId}/archive`,
    );
  },

  // ─── Phase 4: Team Re-registration ───────────────────────────────────────

  /**
   * Re-register a team for a new season
   */
  reRegisterTeam: async (request: ReRegisterTeamRequest): Promise<ReRegisterTeamResponse> => {
    return apiCall<ReRegisterTeamResponse>('post', '/v1/teams/re-register', request);
  },

  /**
   * Form a new team
   */
  formNewTeam: async (request: FormNewTeamRequest): Promise<Team> => {
    return apiCall<Team>('post', '/v1/teams/new', request);
  },

  // ─── League Administrator Management ─────────────────────────────────────

  /**
   * Get the list of administrators for a league (Super Admin or league admin)
   */
  getLeagueAdministrators: async (leagueId: string): Promise<LeagueAdministrator[]> => {
    return apiCall<LeagueAdministrator[]>('get', API_ROUTES.LEAGUE.ADMINISTRATORS(leagueId));
  },

  /**
   * Assign a user as an administrator of a league (Super Admin only)
   */
  assignLeagueAdministrator: async (
    leagueId: string,
    request: AssignLeagueAdminRequest,
  ): Promise<League> => {
    return apiCall<League>('post', API_ROUTES.LEAGUE.ADMINISTRATORS(leagueId), request);
  },

  /**
   * Revoke a user's administrator role for a league (Super Admin only)
   */
  revokeLeagueAdministrator: async (leagueId: string, userId: string): Promise<League> => {
    return apiCall<League>('delete', API_ROUTES.LEAGUE.ADMINISTRATOR(leagueId, userId));
  },

  // ─── All seasons for a league ────────────────────────────────────────────────

  /**
   * Get all seasons for a league (upcoming, active, completed).
   */
  getSeasons: async (leagueId: string): Promise<Season[]> => {
    return apiCall<Season[]>('get', API_ROUTES.LEAGUE.SEASONS(leagueId));
  },

  /**
   * Get the subscribed players/teams for a season, grouped by category (admin).
   */
  getSeasonSubscribers: async (
    leagueId: string,
    seasonId: string,
  ): Promise<SeasonSubscribersResponse> => {
    return apiCall<SeasonSubscribersResponse>(
      'get',
      API_ROUTES.LEAGUE.SEASON_SUBSCRIBERS(leagueId, seasonId),
    );
  },

  // ─── Divisions by category (public) ─────────────────────────────────────────

  /**
   * Get the current (active) season for a league.
   * Returns the season whose id matches league.currentSeasonId, or null.
   */
  getLeagueSeason: async (leagueId: string, seasonId: string): Promise<Season> => {
    return apiCall<Season>('get', `/v1/leagues/${leagueId}/seasons/${seasonId}`);
  },

  /**
   * Get all divisions for a season, optionally filtered by category.
   * Used by DivisionsPage to load tiers for the selected category.
   */
  getDivisionsForSeason: async (
    leagueId: string,
    seasonId: string,
    category?: Category,
  ): Promise<Division[]> => {
    // With a category, use the per-category route. Without one, fetch ALL
    // divisions for the season (across categories) — used by the league detail
    // page to show the full division structure. The category route is scoped to
    // the season so a transition doesn't leak the previous season's divisions.
    if (category) {
      return apiCall<Division[]>(
        'get',
        `/v1/leagues/${leagueId}/categories/${encodeURIComponent(
          category,
        )}/divisions?seasonId=${encodeURIComponent(seasonId)}`,
      );
    }
    return apiCall<Division[]>('get', `/v1/leagues/${leagueId}/seasons/${seasonId}/divisions`);
  },

  /**
   * Get division standings for all divisions within a category's season.
   * Falls back gracefully if the backend returns an error for a division.
   */
  getDivisionStandingsBatch: async (
    leagueId: string,
    seasonId: string,
    divisionIds: string[],
  ): Promise<DivisionStandings[]> => {
    const results = await Promise.allSettled(
      divisionIds.map(divisionId =>
        apiCall<DivisionStandings>(
          'get',
          `/v1/leagues/${leagueId}/seasons/${seasonId}/divisions/${divisionId}/standings`,
        ),
      ),
    );
    return results
      .filter((r): r is PromiseFulfilledResult<DivisionStandings> => r.status === 'fulfilled')
      .map(r => r.value);
  },

  /**
   * Get teams that have a pendingCategory matching the given category but no
   * division assignment yet (i.e. pending teams for this category in this league).
   * Uses the public team list endpoint with query parameters.
   */
  getPendingTeamsForCategory: async (
    leagueId: string,
    category: Category,
    seasonId?: string,
  ): Promise<Team[]> => {
    let url = `/v1/teams?leagueId=${encodeURIComponent(
      leagueId,
    )}&pendingCategory=${encodeURIComponent(category)}&unassigned=true`;
    // Season-scope so the pending list matches the season-scoped admin placement
    // queue (a team pending in another/closed season must not appear here).
    if (seasonId) url += `&seasonId=${encodeURIComponent(seasonId)}`;
    return apiCall<Team[]>('get', url);
  },

  /**
   * Get all divisions standings for a category (returns a flat list of TeamStanding
   * for each division's standing rows).
   * Exported so DivisionsPage can build its per-division standings from the batch.
   */
  getCategoryStandings: async (
    _leagueId: string,
    _seasonId: string,
    _category: Category,
  ): Promise<TeamStanding[]> => {
    // This is a convenience wrapper; actual calls go through getDivisionStandingsBatch.
    return [];
  },

  /**
   * Fetch enrollments for a season (admin only). Optionally filter by status.
   */
  getSeasonEnrollments: async (
    leagueId: string,
    seasonId: string,
    status?: string,
  ): Promise<Enrollment[]> => {
    const base = API_ROUTES.LEAGUE.SEASON_ENROLLMENTS(leagueId, seasonId);
    const url = status ? `${base}?status=${encodeURIComponent(status)}` : base;
    return apiCall<Enrollment[]>('get', url);
  },

  /**
   * Teams queued against this league's ladders — enrolled while no upcoming
   * season existed, awaiting the next one to be created (admin only).
   */
  getLeagueQueue: async (leagueId: string): Promise<Enrollment[]> => {
    return apiCall<Enrollment[]>('get', API_ROUTES.LEAGUE.QUEUE(leagueId));
  },
};

export default leagueService;
